import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { FixDropdownOptionItem } from "../fix-dropdown/FixDropdownOption";
import { Input } from "@headlessui/react";
import { Icon } from "@/elements-react/fix-icon";
import { FixDivider } from "@/elements-react/fix-divider";
import { FixDropdownOption } from "@/elements-react/fix-dropdown";

type Props = {
  type?: string;
  items: Array<FixDropdownOptionItem>;
  onSelect?: (value: string, index: number) => void;
  isSearchable?: boolean;
  withIcon?: boolean;
  onSearch?: (value: string) => void;
  label?: string;
};

export const FixDialogSelector: React.FC<Props> = ({ type = "normal", items, onSelect, isSearchable = true, onSearch, label, withIcon = true }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<FixDropdownOptionItem | undefined>();

  const options = useMemo(() => {
    const lowerCaseTerm = searchTerm.trim().toLowerCase();
    const found = {
      starting: [] as FixDropdownOptionItem[],
      including: [] as FixDropdownOptionItem[],
    };

    if (lowerCaseTerm.length) {
      const matchOption = (key: "label" | "value", item: FixDropdownOptionItem, text: string) => {
        const index = item[key].toString().toLowerCase().indexOf(lowerCaseTerm);
        if (index >= 0) {
          return {
            type: index === 0 ? ("starting" as const) : ("including" as const),
            key,
          };
        }
        return undefined;
      };

      for (const item of items) {
        const matched = matchOption("label", item, lowerCaseTerm) ?? matchOption("value", item, lowerCaseTerm);
        if (!matched) continue;
        if (matched.type === "starting" && matched.key === "label") {
          found.starting.push(item);
        } else {
          found.including.push(item);
        }
      }
    } else {
      found.including.push(...items);
    }

    found.starting.sort((a, b) => a.label.localeCompare(b.label));
    found.including.sort((a, b) => a.label.localeCompare(b.label));

    return [...found.starting, ...found.including];
  }, [items, searchTerm]);

  const handleOptionClick = (value: string, index: number) => {
    setSelected(options[index]);
    onSelect?.(value, index);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value.toLowerCase());
    onSearch?.(value);
  };

  return (
    <div
      className={classNames(styles.root, styles["fix-dropdown-options-container"], {
        [styles[`type-${type}`]]: true,
      })}
    >
      {(type === "parameters-inline" || type === "parameters-screen") && <FixDivider className={styles.dialogDivider}/>}
      {isSearchable && (
        <div className={styles["fix-dropdown-search"]}>
          {withIcon && <Icon name="search-variant" />}
          <Input onInput={(e) => handleSearch((e.target as HTMLInputElement).value)} placeholder={t("0x619y4k02smw9cs.search", "Search")} />
        </div>
      )}
      {type === "normal" && <FixDivider className={styles.dialogDivider} />}
      <div className={styles.options}>
        {options.map((item, index) => (
          <FixDropdownOption
            key={item.value}
            selected={item.selected}
            value={item.value}
            label={item.label}
            icon={item.icon}
            onClick={() => handleOptionClick(item.value as string, index)}
          />
        ))}
      </div>
    </div>
  );
};
